import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";

import {
  type ColumnField,
  columnsUrlParamName,
  getDefaultColumns,
} from "./columnUtils";

interface UseDataTableUrlInitOptions {
  tableId?: string;
  enableColumnSelection?: boolean;
  allColumns: ColumnField[];
}

/**
 * Lightweight hook specifically for URL initialization.
 * Use this in parent components to ensure URL params are set before DataTable renders.
 * DataTable will then use useDataTableColumns for full column management.
 */
export const useDataTableUrlInit = ({
  tableId,
  enableColumnSelection = false,
  allColumns,
}: UseDataTableUrlInitOptions) => {
  const [urlParams, setUrlParams] = useSearchParams();

  useEffect(() => {
    if (!enableColumnSelection || !tableId || !allColumns.length) return;

    const urlColumns = urlParams.get(columnsUrlParamName);
    if (!urlColumns) {
      // If the URL doesn't already have columns set, use cached default columns
      const defaultColumns = getDefaultColumns(tableId, allColumns);
      const newUrlParams = new URLSearchParams(urlParams);
      newUrlParams.set(columnsUrlParamName, defaultColumns.join(","));
      setUrlParams(newUrlParams);
    }
  }, [tableId, enableColumnSelection, allColumns, urlParams, setUrlParams]);
};
